import { useState } from "react";
import { Box, Button, Typography } from "@mui/joy";
import { Camera, ChevronDown, ImageUp } from "lucide-react";

import { AuthenticatedImage } from "..";
import { ImagePreviewModal } from "./ImagePreviewDialog";
import { ImageUploadModal } from "./ImageUploadModal";
import { AlbumGroup } from "./types";
import { useSingleAlbum } from "./hooks";
import { getDisplayAssetId, getDisplayUrl, isPendingUpload } from "../../utils/taskAssetImages";
import { AlbumImageUploadOverlay } from "./AlbumImageUploadOverlay";

interface AlbumCategoryCardProps {
    group: AlbumGroup;
    taskId: string;
    projectId: string;
    onImagesChange: (images: AlbumGroup["images"]) => void;
}

export function AlbumCategoryCard(props: AlbumCategoryCardProps) {
    const { group, taskId, projectId, onImagesChange } = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const {
        selectedImage,
        closePreview,
        openPreview,
        deleteImage,
        isDeleting,
        uploadModalOpen,
        setUploadModalOpen,
        pendingFiles,
        setPendingFiles,
        fileInputRef,
        openFilePicker,
        handleFileInput,
        handleUploadConfirm,
        isUploading,
    } = useSingleAlbum({
        label: group.label,
        taskId,
        projectId,
        images: group.images,
        onImagesChange,
    });

    const photoCount = group.images.length;

    return (
        <Box
            sx={{
                width: "100%",
                ...(isExpanded && {
                    bgcolor: "primary.softBg",
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "primary.outlinedBorder",
                    p: 0.75,
                }),
            }}
        >
            <Box
                onClick={() => setIsExpanded((prev) => !prev)}
                sx={{
                    position: "relative",
                    borderRadius: "4px",
                    overflow: "hidden",
                    cursor: "pointer",
                    ...(isExpanded ? { height: 36 } : { aspectRatio: "1/1" }),
                    "&:hover .cover-image": {
                        transform: isExpanded ? "none" : "scale(1.05)",
                    },
                }}
            >
                <AuthenticatedImage
                    url={group.coverImage}
                    isBackground
                    className="cover-image"
                    borderRadius={0}
                    sx={{
                        width: "100%",
                        height: "100%",
                        backgroundPosition: isExpanded ? "center top" : "center",
                        backgroundSize: "cover",
                        transition: "transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: isExpanded
                            ? "rgba(var(--standard-palette-primary-mainChannel) / 0.65)"
                            : "linear-gradient(180deg, rgba(var(--standard-palette-primary-mainChannel) / 0.1) 0%, rgba(var(--standard-palette-primary-darkChannel) / 0.95) 100%)",
                        backdropFilter: isExpanded ? "blur(2px)" : "none",
                        pointerEvents: "none",
                    }}
                />

                {!isExpanded && photoCount > 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.35)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            borderRadius: "4px",
                            px: 0.75,
                            py: 0.25,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            backdropFilter: "blur(8px)",
                        }}
                    >
                        <Camera size={10} color="white" />
                        <Typography
                            sx={{
                                color: "white",
                                fontSize: "10px",
                                fontWeight: 700,
                                lineHeight: 1,
                            }}
                        >
                            {photoCount}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        px: 1,
                        display: "flex",
                        alignItems: isExpanded ? "center" : "flex-end",
                        justifyContent: "space-between",
                        pb: isExpanded ? 0 : 1,
                    }}
                >
                    <Typography
                        noWrap={isExpanded}
                        sx={{
                            color: "white",
                            fontSize: isExpanded ? "12px" : "14px",
                            fontWeight: 600,
                            lineHeight: 1.2,
                            textShadow: "0 1px 4px rgba(0,0,0,0.8), 0 2px 8px rgba(0,0,0,0.6)",
                            flex: 1,
                            minWidth: 0,
                            ...(!isExpanded && {
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }),
                        }}
                    >
                        {group.title}
                    </Typography>
                    <ChevronDown
                        size={14}
                        color="white"
                        style={{
                            flexShrink: 0,
                            filter: "drop-shadow(0px 1px 3px rgba(0,0,0,0.8))",
                            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                        }}
                    />
                </Box>
            </Box>

            {isExpanded && (
                <Box
                    sx={{
                        mt: 1,
                        animation: "fadeSlideIn 0.2s ease-out",
                        "@keyframes fadeSlideIn": {
                            from: { opacity: 0, transform: "translateY(-4px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    {photoCount > 0 && (
                        <>
                            <Typography
                                level="body-xs"
                                sx={{
                                    color: "text.tertiary",
                                    mb: 0.5,
                                    px: 0.5,
                                    fontWeight: 500,
                                }}
                            >
                                {photoCount} photo{photoCount !== 1 ? "s" : ""}
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, 1fr)",
                                    gap: 0.5,
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                }}
                            >
                                {group.images.map((image, index) => (
                                    <Box
                                        key={`${group.id}-${index}`}
                                        onClick={() => {
                                            if (!isPendingUpload(image)) {
                                                openPreview(image);
                                            }
                                        }}
                                        sx={{
                                            position: "relative",
                                            aspectRatio: "1/1",
                                            cursor: isPendingUpload(image)
                                                ? "default"
                                                : "pointer",
                                            bgcolor: "background.level2",
                                            "&:active": {
                                                opacity: isPendingUpload(image) ? 1 : 0.7,
                                            },
                                        }}
                                    >
                                        <AuthenticatedImage
                                            assetId={getDisplayAssetId(image)}
                                            url={getDisplayUrl(image)}
                                            alt={`Photo ${index + 1}`}
                                            borderRadius={0}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                        {isPendingUpload(image) && (
                                            <AlbumImageUploadOverlay />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}

                    <Box sx={{ mt: 1.5 }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif,.heic,.heif"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileInput}
                        />
                        <Button
                            variant="soft"
                            color="primary"
                            fullWidth
                            size="md"
                            startDecorator={<ImageUp size={18} />}
                            onClick={openFilePicker}
                            disabled={isUploading}
                            sx={{
                                borderRadius: "4px",
                                py: 1,
                                fontSize: "14px",
                                fontWeight: 600,
                            }}
                        >
                            Upload Photos
                        </Button>
                    </Box>
                </Box>
            )}

            <ImagePreviewModal
                image={selectedImage}
                onClose={closePreview}
                onDelete={() => deleteImage()}
                isDeleting={isDeleting}
            />
            <ImageUploadModal
                open={uploadModalOpen}
                files={pendingFiles}
                onClose={() => {
                    setUploadModalOpen(false);
                    setPendingFiles([]);
                }}
                onUpload={handleUploadConfirm}
            />
        </Box>
    );
}
